import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageHero, PageBody, formatDate, slugify } from "./_shared";
import { useNursing } from "./NursingLayout";
import { Trophy, Calendar } from "lucide-react";

export default function CampusEngagements() {
  const data = useNursing();
  const { typeSlug } = useParams();
  const navigate = useNavigate();
  const all = data.events || [];
  const list = useMemo(() => {
    if (!typeSlug) return all;
    return all.filter((e) => slugify(e.event_type || "") === typeSlug.toLowerCase());
  }, [all, typeSlug]);
  const activeTypeLabel = typeSlug ? list[0]?.event_type || typeSlug : "";

  const [activeId, setActiveId] = useState(list[0]?.id || null);
  useEffect(() => { setActiveId(list[0]?.id || null); }, [typeSlug, list]);
  const active = list.find((e) => e.id === activeId) || null;

  return (
    <div data-testid="nur-engagements">
      <PageHero eyebrow={typeSlug ? `Stories · ${activeTypeLabel}` : "Stories"} title="Campus Engagements" accent="orange" />
      <PageBody>
        {typeSlug && (
          <div className="mb-4 flex items-center justify-between bg-[#E0F5EE] border border-[#0FB28A]/40 rounded-lg px-4 py-2 text-sm">
            <span className="text-[#0F2A5F]">Showing <span className="font-semibold">{activeTypeLabel}</span> ({list.length})</span>
            <button onClick={() => navigate("../campus-engagements")} className="text-[11px] uppercase tracking-wider font-bold text-[#0E916F] hover:text-[#0F2A5F]">Show All</button>
          </div>
        )}
        {list.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center text-sm text-[#475569] ring-1 ring-[#E2E8F0]">
            Campus stories will appear here once published.
          </div>
        ) : (
          <div className="grid md:grid-cols-[300px_1fr] gap-6">
            <aside className="bg-white rounded-2xl ring-1 ring-[#E2E8F0] md:sticky md:top-32 self-start overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-[#E2E8F0] text-[10px] uppercase tracking-[0.25em] text-[#0FB28A] font-bold">
                {typeSlug ? activeTypeLabel : "All"} ({list.length})
              </div>
              <ul className="divide-y divide-[#E2E8F0] max-h-[600px] overflow-y-auto">
                {list.map((e) => {
                  const isA = e.id === activeId;
                  const isAch = /achievement/i.test(e.event_type || "");
                  return (
                    <li key={e.id}>
                      <button onClick={() => setActiveId(e.id)}
                        className={`w-full text-left px-4 py-3 flex items-start gap-3 ${isA ? "bg-[#E0F5EE]" : "hover:bg-[#F4F6FA]"}`}>
                        <span className={`shrink-0 mt-0.5 w-7 h-7 rounded-full flex items-center justify-center ${isAch ? "bg-[#0FB28A]" : "bg-[#0FB28A]"} text-white shadow-sm`}>
                          {isAch ? <Trophy size={12} /> : <Calendar size={12} />}
                        </span>
                        <div className="min-w-0 flex-1">
                          {e.event_type && (
                            <div className={`text-[9px] tracking-[0.2em] uppercase font-bold mb-0.5 ${isAch ? "text-[#0E916F]" : "text-[#0E916F]"}`}>
                              {e.event_type}
                            </div>
                          )}
                          <div className={`text-sm leading-snug ${isA ? "text-[#0F2A5F] font-semibold" : "text-[#475569]"}`}>{e.title}</div>
                          <div className="text-[11px] text-[#475569] mt-0.5">{formatDate(e.event_date)}</div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>
            {active && (
              <article className="bg-white rounded-2xl ring-1 ring-[#E2E8F0] p-7 shadow-sm">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#0FB28A] font-bold">{active.event_type}</div>
                <h2 className="mt-2 font-['Poppins',sans-serif] font-bold text-2xl text-[#0F2A5F]">{active.title}</h2>
                <div className="mt-1 text-[12px] text-[#475569]">{formatDate(active.event_date)}</div>
                {active.description && <p className="mt-5 text-sm text-[#475569] leading-relaxed whitespace-pre-line">{active.description}</p>}
                {Array.isArray(active.photos) && active.photos.length > 0 && (
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {active.photos.map((p, i) => (
                      <img key={i} src={p.url || p} alt="" className="aspect-video object-cover rounded-lg ring-1 ring-[#E2E8F0]" loading="lazy" />
                    ))}
                  </div>
                )}
              </article>
            )}
          </div>
        )}
      </PageBody>
    </div>
  );
}
